import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  Divider,
  Tooltip,
  Collapse,
  Autocomplete,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GpsFixedOutlinedIcon from '@mui/icons-material/GpsFixedOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { type BoundaryRecord } from './BoundaryImporterView';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { BoundaryMapPicker, type PolygonVertex } from './BoundaryMapPicker';
import { useAppSelector } from 'store/hook';
import LocationImporterAPI from 'api/location-importer.api';
import GradeImporterAPI from 'api/grade-importer.api';

interface LocationOption {
  locationId: string;
  name: string;
  locationType: string;
  easting: number;
  northing: number;
  centroidLat: number | null;
  centroidLon: number | null;
}

interface GradeOption {
  id: string;
  name: string;
  locationId: string | null;
  locationName: string;
}

interface CoordRow {
  pos: string;
  easting: string;
  northing: string;
  elevation: string;
}

interface CoordRowErrors {
  pos?: string;
  easting?: string;
  northing?: string;
  elevation?: string;
}

const emptyRow = (pos: number): CoordRow => ({
  pos: String(pos),
  easting: '',
  northing: '',
  elevation: '',
});

type InputMode = 'none' | 'map' | 'manual';
type TargetType = 'location' | 'grade';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (rows: BoundaryRecord[]) => void;
}

export const AddBoundaryModal = ({
  open,
  onClose,
  onAdd,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { customer } = useAppSelector((s) => s.authReducer);
  const customerId = customer.id;
  const locationApi = useRef(new LocationImporterAPI()).current;
  const gradeApi = useRef(new GradeImporterAPI()).current;

  const [targetType, setTargetType] = useState<TargetType>('location');

  // Location state
  const [selectedLocation, setSelectedLocation] =
    useState<LocationOption | null>(null);
  const [locationNameError, setLocationNameError] = useState('');
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Grade state
  const [selectedGrade, setSelectedGrade] = useState<GradeOption | null>(null);
  const [gradeError, setGradeError] = useState('');
  const [gradeOptions, setGradeOptions] = useState<GradeOption[]>([]);
  const [gradesLoading, setGradesLoading] = useState(false);

  const [inputMode, setInputMode] = useState<InputMode>('none');
  const [rows, setRows] = useState<CoordRow[]>([]);
  const [rowErrors, setRowErrors] = useState<CoordRowErrors[]>([]);

  useEffect(() => {
    if (!open || !customerId) return;
    if (locationOptions.length === 0) {
      setLocationsLoading(true);
      locationApi
        .getLocations()
        .then((data) => {
          setLocationOptions(
            data.map((r) => ({
              locationId: r.locationId,
              name: r.name ?? '',
              locationType: r.locationType ?? '',
              easting: r.easting ?? 0,
              northing: r.northing ?? 0,
              centroidLat: r.centroidLat,
              centroidLon: r.centroidLon,
            }))
          );
        })
        .catch(() => {})
        .finally(() => {
          setLocationsLoading(false);
        });
    }
    if (gradeOptions.length === 0) {
      setGradesLoading(true);
      gradeApi
        .getGrades()
        .then((data) => {
          setGradeOptions(
            data.map((g) => ({
              id: g.id,
              name: g.name,
              locationId: g.locationId,
              locationName: g.locationName ?? '',
            }))
          );
        })
        .catch(() => {})
        .finally(() => {
          setGradesLoading(false);
        });
    }
  }, [
    open,
    customerId,
    locationOptions.length,
    gradeOptions.length,
    locationApi,
    gradeApi,
  ]);

  // When grade mode: find the grade's associated location for the map pin
  const gradeLocation = useMemo<LocationOption | null>(() => {
    if (!selectedGrade?.locationId) return null;
    return (
      locationOptions.find((l) => l.locationId === selectedGrade.locationId) ??
      null
    );
  }, [selectedGrade, locationOptions]);

  const resetForm = () => {
    setTargetType('location');
    setSelectedLocation(null);
    setSelectedGrade(null);
    setLocationNameError('');
    setGradeError('');
    setInputMode('none');
    setRows([]);
    setRowErrors([]);
    setGradeOptions([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleTargetTypeChange = (
    _: React.MouseEvent,
    val: TargetType | null
  ) => {
    if (!val) return;
    setTargetType(val);
    setSelectedLocation(null);
    setSelectedGrade(null);
    setLocationNameError('');
    setGradeError('');
    setInputMode('none');
    setRows([]);
    setRowErrors([]);
  };

  const handleLocationSelect = (val: LocationOption | null) => {
    setSelectedLocation(val);
    if (val) setLocationNameError('');
    setInputMode('none');
    setRows([]);
    setRowErrors([]);
  };

  const handleGradeSelect = (val: GradeOption | null) => {
    setSelectedGrade(val);
    if (val) setGradeError('');
    setInputMode('none');
    setRows([]);
    setRowErrors([]);
  };

  const chooseManual = () => {
    setInputMode('manual');
    setRows([emptyRow(1)]);
    setRowErrors([{}]);
  };

  const chooseMap = () => {
    setInputMode('map');
    setRows([]);
    setRowErrors([]);
  };

  const handleMapDraw = useCallback((vertices: PolygonVertex[]) => {
    const newRows = vertices.map((v, i) => ({
      pos: String(i + 1),
      easting: v.easting.toFixed(3),
      northing: v.northing.toFixed(3),
      elevation: '',
    }));
    setRows(newRows);
    setRowErrors(newRows.map(() => ({})));
    setInputMode('manual');
  }, []);

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow(prev.length + 1)]);
    setRowErrors((prev) => [...prev, {}]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setRowErrors((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx: number, key: keyof CoordRow, value: string) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );
    setRowErrors((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [key]: undefined } : e))
    );
  };

  const hasSelection =
    targetType === 'location' ? !!selectedLocation : !!selectedGrade;

  const validate = (): boolean => {
    let valid = true;
    if (targetType === 'location' && !selectedLocation) {
      setLocationNameError(t('boundary_importer_err_required'));
      valid = false;
    }
    if (targetType === 'grade' && !selectedGrade) {
      setGradeError(t('boundary_importer_err_required'));
      valid = false;
    }
    const nextRowErrors: CoordRowErrors[] = rows.map((r) => {
      const e: CoordRowErrors = {};
      const posNum = parseInt(r.pos, 10);
      if (!r.pos.trim()) {
        e.pos = t('boundary_importer_err_required');
      } else if (
        isNaN(posNum) ||
        posNum <= 0 ||
        !Number.isInteger(Number(r.pos))
      ) {
        e.pos = t('boundary_importer_err_positive_int');
      }
      if (!r.easting.trim()) {
        e.easting = t('boundary_importer_err_required');
      } else if (isNaN(parseFloat(r.easting))) {
        e.easting = t('boundary_importer_err_number');
      }
      if (!r.northing.trim()) {
        e.northing = t('boundary_importer_err_required');
      } else if (isNaN(parseFloat(r.northing))) {
        e.northing = t('boundary_importer_err_number');
      }
      if (!r.elevation.trim()) {
        e.elevation = t('boundary_importer_err_required');
      } else if (isNaN(parseFloat(r.elevation))) {
        e.elevation = t('boundary_importer_err_number');
      }
      if (Object.keys(e).length > 0) valid = false;
      return e;
    });
    setRowErrors(nextRowErrors);
    if (rows.length === 0) valid = false;
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const records: BoundaryRecord[] = rows.map((r) => ({
      id: crypto.randomUUID(),
      gradeName: targetType === 'grade' ? selectedGrade?.name ?? '' : '',
      gradeId: targetType === 'grade' ? selectedGrade?.id ?? null : null,
      locationName:
        targetType === 'location'
          ? selectedLocation?.name ?? ''
          : selectedGrade?.locationName ?? '',
      locationId:
        targetType === 'location'
          ? selectedLocation?.locationId ?? null
          : selectedGrade?.locationId ?? null,
      pos: parseInt(r.pos, 10),
      easting: parseFloat(r.easting),
      northing: parseFloat(r.northing),
      elevation: parseFloat(r.elevation),
    }));
    onAdd(records);
  };

  const hasRows = rows.length > 0;
  const canSubmit = hasSelection && hasRows;

  // Silence unused import warning — clientThemePalette used only for palette var
  const _palette = clientThemePalette[CLIENT_THEME].light;
  void _palette;

  // Centroid / easting for BoundaryMapPicker
  const mapCentroidLat =
    targetType === 'location'
      ? selectedLocation?.centroidLat ?? null
      : gradeLocation?.centroidLat ?? null;
  const mapCentroidLon =
    targetType === 'location'
      ? selectedLocation?.centroidLon ?? null
      : gradeLocation?.centroidLon ?? null;
  const mapEasting =
    targetType === 'location'
      ? selectedLocation?.easting
      : gradeLocation?.easting;
  const mapNorthing =
    targetType === 'location'
      ? selectedLocation?.northing
      : gradeLocation?.northing;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={inputMode === 'map' ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.25),
            }}
          >
            <GpsFixedOutlinedIcon
              sx={{ fontSize: 18, color: 'primary.main' }}
            />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('boundary_importer_add_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('boundary_importer_add_subtitle')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* ── Target type toggle ── */}
      <Box sx={{ px: 3, pb: 1.5 }}>
        <ToggleButtonGroup
          value={targetType}
          exclusive
          onChange={handleTargetTypeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              textTransform: 'none',
              gap: 0.6,
            },
          }}
        >
          <ToggleButton value="location">
            <FmdGoodOutlinedIcon sx={{ fontSize: 15 }} />
            {t('boundary_importer_type_location')}
          </ToggleButton>
          <ToggleButton value="grade">
            <LayersOutlinedIcon sx={{ fontSize: 15 }} />
            {t('boundary_importer_type_grade')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Step 1: Location or Grade selector ── */}
      <Box
        sx={{
          mx: 3,
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.6),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
        >
          {t('boundary_importer_section_info')}
        </Typography>
        {targetType === 'location' && selectedLocation && (
          <Chip
            icon={<FmdGoodOutlinedIcon sx={{ fontSize: 13 }} />}
            label={selectedLocation.locationType}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.68rem', height: 20 }}
          />
        )}
        {targetType === 'grade' && selectedGrade && (
          <Chip
            icon={<LayersOutlinedIcon sx={{ fontSize: 13 }} />}
            label={selectedGrade.locationName || '—'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.68rem', height: 20 }}
          />
        )}
      </Box>

      <DialogContent sx={{ px: 3, pt: 1.5, pb: 1.5 }}>
        {/* Location autocomplete */}
        {targetType === 'location' && (
          <Autocomplete
            options={locationOptions}
            getOptionLabel={(opt) => opt.name}
            value={selectedLocation}
            onChange={(_, val) => {
              handleLocationSelect(val);
            }}
            loading={locationsLoading}
            isOptionEqualToValue={(opt, val) =>
              opt.locationId === val.locationId
            }
            noOptionsText={
              locationsLoading
                ? t('boundary_importer_loading_locations')
                : t('boundary_importer_no_locations')
            }
            renderOption={(props, opt) => (
              <li {...props} key={opt.locationId}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    py: 0.2,
                  }}
                >
                  <FmdGoodOutlinedIcon
                    sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                    {opt.name}
                  </Typography>
                  {opt.locationType && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 0.8,
                        py: 0.15,
                        borderRadius: 1,
                        bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.67rem',
                        flexShrink: 0,
                      }}
                    >
                      {opt.locationType}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('boundary_importer_field_location_name')}
                size="small"
                error={!!locationNameError}
                helperText={locationNameError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {locationsLoading && (
                        <CircularProgress size={14} sx={{ mr: 0.5 }} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        {/* Grade autocomplete */}
        {targetType === 'grade' && (
          <Autocomplete
            options={gradeOptions}
            getOptionLabel={(opt) => opt.name}
            value={selectedGrade}
            onChange={(_, val) => {
              handleGradeSelect(val);
            }}
            loading={gradesLoading}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            noOptionsText={
              gradesLoading
                ? t('boundary_importer_loading_grades')
                : t('boundary_importer_no_grades')
            }
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    py: 0.2,
                  }}
                >
                  <LayersOutlinedIcon
                    sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                    {opt.name}
                  </Typography>
                  {opt.locationName && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 0.8,
                        py: 0.15,
                        borderRadius: 1,
                        bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.67rem',
                        flexShrink: 0,
                      }}
                    >
                      {opt.locationName}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('boundary_importer_field_grade')}
                size="small"
                error={!!gradeError}
                helperText={gradeError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {gradesLoading && (
                        <CircularProgress size={14} sx={{ mr: 0.5 }} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      </DialogContent>

      {/* ── Step 2: Method picker ── */}
      <Collapse in={hasSelection && inputMode === 'none'} unmountOnExit>
        <Box sx={{ px: 3, pb: 3, pt: 0.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              display: 'block',
              mb: 1.5,
            }}
          >
            {t('boundary_importer_choose_method')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              onClick={chooseMap}
              sx={{
                flex: 1,
                cursor: 'pointer',
                borderRadius: 2.5,
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.3),
                bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.09),
                  transform: 'translateY(-1px)',
                  boxShadow: (th) =>
                    `0 4px 16px ${alpha(th.palette.primary.main, 0.2)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                  border: '1px solid',
                  borderColor: (th) => alpha(th.palette.primary.main, 0.25),
                }}
              >
                <MapOutlinedIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {t('boundary_importer_method_map')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                lineHeight={1.5}
              >
                {t('boundary_importer_method_map_desc')}
              </Typography>
            </Box>

            <Box
              onClick={chooseManual}
              sx={{
                flex: 1,
                cursor: 'pointer',
                borderRadius: 2.5,
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.divider, 0.6),
                bgcolor: 'transparent',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: 'text.secondary',
                  bgcolor: (th) => alpha(th.palette.action.hover, 0.5),
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (th) => alpha(th.palette.text.secondary, 0.08),
                  border: '1px solid',
                  borderColor: (th) => alpha(th.palette.divider, 0.5),
                }}
              >
                <EditOutlinedIcon
                  sx={{ fontSize: 24, color: 'text.secondary' }}
                />
              </Box>
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {t('boundary_importer_method_manual')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                lineHeight={1.5}
              >
                {t('boundary_importer_method_manual_desc')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* ── Step 3a: Map drawing ── */}
      <Collapse in={hasSelection && inputMode === 'map'} unmountOnExit>
        <Box>
          <Box
            sx={{
              mx: 3,
              mb: 1,
              pb: 0.5,
              borderBottom: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.6),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
            >
              {t('boundary_importer_section_coords')}
            </Typography>
            <Button
              size="small"
              startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
              onClick={() => {
                setInputMode('none');
                setRows([]);
                setRowErrors([]);
              }}
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
                px: 1,
                py: 0.3,
                minWidth: 0,
              }}
            >
              {t('boundary_importer_change_method')}
            </Button>
          </Box>
          <Box sx={{ px: 3, pb: 1 }}>
            <BoundaryMapPicker
              onDraw={handleMapDraw}
              locationEasting={mapEasting}
              locationNorthing={mapNorthing}
              centroidLat={mapCentroidLat}
              centroidLon={mapCentroidLon}
            />
          </Box>
        </Box>
      </Collapse>

      {/* ── Step 3b: Manual coordinate rows ── */}
      <Collapse in={hasSelection && inputMode === 'manual'} unmountOnExit>
        <Box>
          <Box
            sx={{
              mx: 3,
              mt: 0.5,
              mb: 1,
              pb: 0.5,
              borderBottom: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.6),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
            >
              {t('boundary_importer_section_coords')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
                onClick={() => {
                  setInputMode('none');
                  setRows([]);
                  setRowErrors([]);
                }}
                sx={{
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  px: 1,
                  py: 0.3,
                  minWidth: 0,
                }}
              >
                {t('boundary_importer_change_method')}
              </Button>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={addRow}
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  px: 1,
                  py: 0.3,
                  minWidth: 0,
                }}
              >
                {t('boundary_importer_add_row')}
              </Button>
            </Box>
          </Box>

          <DialogContent
            sx={{ px: 3, pt: 1, pb: 2, maxHeight: 360, overflowY: 'auto' }}
          >
            {rows.map((row, idx) => (
              <Box key={idx}>
                {idx > 0 && <Divider sx={{ my: 1.5, opacity: 0.4 }} />}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{
                      pt: 1.2,
                      minWidth: 20,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}.
                  </Typography>
                  <Grid container spacing={1.5} sx={{ flex: 1 }}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={t('boundary_importer_field_pos')}
                        fullWidth
                        size="small"
                        type="number"
                        value={row.pos}
                        onChange={(e) => {
                          updateRow(idx, 'pos', e.target.value);
                        }}
                        error={!!rowErrors[idx]?.pos}
                        helperText={rowErrors[idx]?.pos}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={t('boundary_importer_field_easting')}
                        fullWidth
                        size="small"
                        type="number"
                        value={row.easting}
                        onChange={(e) => {
                          updateRow(idx, 'easting', e.target.value);
                        }}
                        error={!!rowErrors[idx]?.easting}
                        helperText={rowErrors[idx]?.easting}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={t('boundary_importer_field_northing')}
                        fullWidth
                        size="small"
                        type="number"
                        value={row.northing}
                        onChange={(e) => {
                          updateRow(idx, 'northing', e.target.value);
                        }}
                        error={!!rowErrors[idx]?.northing}
                        helperText={rowErrors[idx]?.northing}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label={t('boundary_importer_field_elevation')}
                        fullWidth
                        size="small"
                        type="number"
                        value={row.elevation}
                        onChange={(e) => {
                          updateRow(idx, 'elevation', e.target.value);
                        }}
                        error={!!rowErrors[idx]?.elevation}
                        helperText={rowErrors[idx]?.elevation}
                      />
                    </Grid>
                  </Grid>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          removeRow(idx);
                        }}
                        disabled={rows.length === 1}
                        sx={{
                          mt: 0.5,
                          color: 'error.main',
                          opacity: rows.length === 1 ? 0.3 : 1,
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </DialogContent>
        </Box>
      </Collapse>

      {/* ── Actions ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" color="text.disabled">
          {hasRows
            ? `${rows.length} ${
                rows.length === 1
                  ? t('boundary_importer_coord_singular')
                  : t('boundary_importer_coord_plural')
              }`
            : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('boundary_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('boundary_importer_btn_add')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
